import React, { useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import apiClient from '../../pages/keycloak/axiosInstance';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const UncountedModules = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canSubmit, setCanSubmit] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const storedRoles = sessionStorage.getItem('userRoles');
    if (storedRoles) {
      let roles = [];
      try {
        const parsed = JSON.parse(storedRoles);
        if (Array.isArray(parsed)) roles = parsed;
      } catch {
        roles = storedRoles.split(',').map(r => r.trim());
      }
      setCanSubmit(roles.includes('Unaccounted_Modules_Edit'));
    }
  }, []);

  const statusOptions = ['', 'Theft', 'In Store', 'Others'];

  useEffect(() => {
    const fetchUnaccountedModules = async () => {
      try {
        const projectId = localStorage.getItem('selectedProject');
        if (!projectId) { setError('Project ID not found in local storage'); return; }
        setLoading(true);
        setError(null);
        const response = await apiClient.post(`find_unaccounted_modules`, { projectId });
        if (response.data.status === 'success' && Array.isArray(response.data.data.unaccounted_modules)) {
          let srNoCounter = 1;
          const modules = response.data.data.unaccounted_modules.flatMap((entry) =>
            entry.unaccounted_modules.flatMap((item, index) =>
              item.modules.map((module, moduleIndex) => ({
                id: `${entry.contractor}_${item.pallet}_${module}_${index}_${moduleIndex}`,
                srNo: srNoCounter++,
                pallet: item.pallet,
                serialNumber: module,
                contractor: entry.contractor,
                status: '',
                remarks: '',
              }))
            )
          );
          setTableData(modules);
          if (modules.length === 0) setError('No unaccounted modules found for this project');
        } else {
          setError(response.data.message || 'Failed to fetch unaccounted modules');
        }
      } catch (err) {
        console.error('Error fetching unaccounted modules:', err);
        setError('Failed to fetch unaccounted modules');
      } finally {
        setLoading(false);
      }
    };
    fetchUnaccountedModules();
  }, []);

  const handleStatusChange = (id, value) =>
    setTableData(prev => prev.map(row => row.id === id ? { ...row, status: value } : row));

  const handleRemarksChange = (id, value) =>
    setTableData(prev => prev.map(row => row.id === id ? { ...row, remarks: value } : row));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const projectId = localStorage.getItem('selectedProject');
      if (!projectId) { setError('Project ID not found in local storage'); return; }
      const reconciliations = tableData
        .filter(row => row.status)
        .map(row => ({
          pallet: row.pallet,
          module: row.serialNumber,
          contractor: row.contractor,
          reconcile: row.status.toLowerCase().replace(' ', '_'),
          remarks: row.remarks || '',
        }));
      if (reconciliations.length === 0) { setError('No modules with status selected to reconcile'); return; }
      setLoading(true);
      setError(null);
      const response = await apiClient.post(`reconcile_unaccounted_modules`, { projectId, reconciliations });
      if (response.data.status === 'success') {
        setTableData([]);
        alert('Data submitted successfully!');
        window.location.reload();
      } else {
        setError(response.data.message || 'Failed to reconcile modules');
      }
    } catch (err) {
      console.error('Error reconciling modules:', err);
      setError('Failed to reconcile modules');
    } finally {
      setLoading(false);
    }
  };

  // ── Pagination calculations ──────────────────────────────────────────────
  const totalRows = tableData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const paginatedData = tableData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1); // reset to first page
  };

  // Build page number buttons (show max 5 around current)
  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);

    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="flex justify-center text-3xl font-bold text-gray-800 mb-6">Unaccounted Modules</h1>

      {loading && (
        <Typography align="center" color="textSecondary" style={{ marginBottom: '20px' }}>
          Loading...
        </Typography>
      )}
      {error && (
        <Typography color="error" align="center" style={{ marginBottom: '20px' }}>
          {error}
        </Typography>
      )}

      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        {!canSubmit && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  Authorization Error: You do not have permission to submit this form.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md mb-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-800">
            <tr>
              {['Sr. No', 'Pallet', 'Contractor', 'Serial Number', 'Status', 'Remarks'].map(h => (
                <th key={h} className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.length > 0 ? (
              paginatedData.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-center text-sm text-gray-900">{row.srNo}</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">{row.pallet}</td>
                  <td className="px-6 py-3 text-center text-xs font-medium">{row.contractor}</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">{row.serialNumber}</td>
                  <td className="px-6 py-4 text-center text-sm">
                    <select
                      value={row.status}
                      onChange={(e) => handleStatusChange(row.id, e.target.value)}
                      className="border border-gray-300 rounded-md p-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 w-full"
                      disabled={loading}
                    >
                      {statusOptions.map(option => (
                        <option key={option} value={option}>{option || 'Select Status'}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-center text-sm">
                    <textarea
                      value={row.remarks}
                      onChange={(e) => handleRemarksChange(row.id, e.target.value)}
                      className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 w-full resize-y"
                      rows="2"
                      placeholder="Enter remarks"
                      disabled={loading}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination Bar ── */}
      {totalRows > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 px-4 py-3 mb-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          
          {/* Left: rows info + rows-per-page */}
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span>
              Showing <span className="font-semibold text-gray-800">{startIndex + 1}</span>–<span className="font-semibold text-gray-800">{endIndex}</span> of <span className="font-semibold text-gray-800">{totalRows}</span> entries
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 hidden sm:inline">|</span>
              <label className="text-gray-500 hidden sm:inline">Rows:</label>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white cursor-pointer"
              >
                {ROWS_PER_PAGE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Right: page controls */}
          <div className="flex items-center gap-1">
            {/* First */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="px-2 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="First page"
            >
              «
            </button>
            {/* Prev */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-medium"
            >
              ‹ Prev
            </button>

            {/* Page numbers */}
            {getPageNumbers().map((page, i) =>
              page === '...' ? (
                <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-gray-400 select-none">…</span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors
                    ${currentPage === page
                      ? 'bg-gray-800 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  {page}
                </button>
              )
            )}

            {/* Next */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-medium"
            >
              Next ›
            </button>
            {/* Last */}
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Last page"
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end items-center gap-4">
        {canSubmit && (
          <button
            onClick={handleSubmit}
            className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={loading}
          >
            Submit
          </button>
        )}
      </div>
    </div>
  );
};

export default UncountedModules;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { Typography } from '@mui/material';
// import apiClient from '../../pages/keycloak/axiosInstance';

// const UncountedModules = () => {
//   const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
//   const [tableData, setTableData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   // Role check: Show Submit button only if user has Unaccounted_Modules_Edit
//   const [canSubmit, setCanSubmit] = useState(false);

//   const baseURL = process.env.REACT_APP_API_BASE_URL;

//   // Check role on mount
//   useEffect(() => {
//     const storedRoles = sessionStorage.getItem("userRoles");
//     if (storedRoles) {
//       let roles = [];
//       try {
//         const parsed = JSON.parse(storedRoles);
//         if (Array.isArray(parsed)) roles = parsed;
//       } catch {
//         roles = storedRoles.split(",").map(r => r.trim());
//       }
//       setCanSubmit(roles.includes("Unaccounted_Modules_Edit"));
//     }
//   }, []);

//   const statusOptions = ['', 'Theft', 'In Store', 'Others'];

//   useEffect(() => {
//     const fetchUnaccountedModules = async () => {
//       try {
//         const projectId = localStorage.getItem('selectedProject');
//         if (!projectId) {
//           setError('Project ID not found in local storage');
//           return;
//         }

//         setLoading(true);
//         setError(null);

//         const response = await apiClient.post(`find_unaccounted_modules`, {
//           projectId,
//         });

//         if (response.data.status === 'success' && Array.isArray(response.data.data.unaccounted_modules)) {
//           let srNoCounter = 1;
//           const modules = response.data.data.unaccounted_modules.flatMap((entry) =>
//             entry.unaccounted_modules.flatMap((item, index) =>
//               item.modules.map((module, moduleIndex) => ({
//                 id: `${entry.contractor}_${item.pallet}_${module}_${index}_${moduleIndex}`,
//                 srNo: srNoCounter++,
//                 pallet: item.pallet,
//                 serialNumber: module,
//                 contractor: entry.contractor,
//                 status: '',
//                 remarks: '',
//               }))
//             )
//           );
//           setTableData(modules);
//           if (modules.length === 0) {
//             setError('No unaccounted modules found for this project');
//           }
//         } else {
//           setError(response.data.message || 'Failed to fetch unaccounted modules');
//         }
//       } catch (err) {
//         console.error('Error fetching unaccounted modules:', err);
//         setError('Failed to fetch unaccounted modules');
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchUnaccountedModules();
//   }, []);

//   const handleStatusChange = (id, value) => {
//     setTableData((prevData) =>
//       prevData.map((row) =>
//         row.id === id ? { ...row, status: value } : row
//       )
//     );
//   };

//   const handleRemarksChange = (id, value) => {
//     setTableData((prevData) =>
//       prevData.map((row) =>
//         row.id === id ? { ...row, remarks: value } : row
//       )
//     );
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const projectId = localStorage.getItem('selectedProject');
//       if (!projectId) {
//         setError('Project ID not found in local storage');
//         return;
//       }

//       const reconciliations = tableData
//         .filter((row) => row.status)
//         .map((row) => ({
//           pallet: row.pallet,
//           module: row.serialNumber,
//           contractor: row.contractor,
//           reconcile: row.status.toLowerCase().replace(' ', '_'),
//           remarks: row.remarks || '',
//         }));

//       if (reconciliations.length === 0) {
//         setError('No modules with status selected to reconcile');
//         return;
//       }

//       setLoading(true);
//       setError(null);

//       const payload = { projectId, reconciliations };
//       const response = await apiClient.post(`reconcile_unaccounted_modules`, payload);
      
//       if (response.data.status === 'success') {
//         setTableData([]);
//         setError(null);
//         alert('Data submitted successfully!');
//         window.location.reload();
//       } else {
//         setError(response.data.message || 'Failed to reconcile modules');
//       }
//     } catch (err) {
//       console.error('Error reconciling modules:', err);
//       setError('Failed to reconcile modules');
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="p-6 bg-gray-100 min-h-screen">
//       <h1 className="flex justify-center text-3xl font-bold text-gray-800 mb-6">Unaccounted Modules</h1>

//       {loading && (
//         <Typography align="center" color="textSecondary" style={{ marginBottom: '20px' }}>
//           Loading...
//         </Typography>
//       )}
//       {error && (
//         <Typography color="error" align="center" style={{ marginBottom: '20px' }}>
//           {error}
//         </Typography>
//       )}

//       <div className="bg-white p-4 rounded-lg shadow-md mb-6">
//         {/* Authorization alert - shown only when user lacks permission */}
//         {!canSubmit && (
//           <div className="rounded-md bg-red-50 p-4 mb-6">
//             <div className="flex">
//               <div className="flex-shrink-0">
//                 <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
//                   <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
//                 </svg>
//               </div>
//               <div className="ml-3">
//                 <p className="text-sm font-medium text-red-800">
//                   Authorization Error: You do not have permission to submit this form.
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Date field (unchanged but now inside the same card) */}
//         <div className="flex flex-wrap gap-4 items-center">
//           <div className="flex flex-col">
//             <label className="text-sm font-medium text-gray-700 mb-1">Date</label>
//             <input
//               type="date"
//               value={date}
//               onChange={(e) => setDate(e.target.value)}
//               className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
//               disabled={loading}
//               readOnly
//             />
//           </div>
//         </div>
//       </div>

//       <div className="bg-white rounded-lg shadow-md mb-6 overflow-x-auto">
//         <table className="min-w-full divide-y divide-gray-200">
//           <thead className="bg-gray-800">
//             <tr>
//               <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Sr. No</th>
//               <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Pallet</th>
//               <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Contractor</th>
//               <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Serial Number</th>
//               <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Status</th>
//               <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Remarks</th>
//             </tr>
//           </thead>
//           <tbody className="bg-white divide-y divide-gray-200">
//             {tableData.length > 0 ? (
//               tableData.map((row) => (
//                 <tr key={row.id} className="hover:bg-gray-50">
//                   <td className="px-6 py-4 text-center text-sm text-gray-900">{row.srNo}</td>
//                   <td className="px-6 py-4 text-center text-sm text-gray-900">{row.pallet}</td>
//                   <td className="px-6 py-3 text-center text-xs font-medium">{row.contractor}</td>
//                   <td className="px-6 py-4 text-center text-sm text-gray-900">{row.serialNumber}</td>
//                   <td className="px-6 py-4 text-center text-sm">
//                     <select
//                       value={row.status}
//                       onChange={(e) => handleStatusChange(row.id, e.target.value)}
//                       className="border border-gray-300 rounded-md p-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 w-full"
//                       disabled={loading}
//                     >
//                       {statusOptions.map((option) => (
//                         <option key={option} value={option}>
//                           {option || 'Select Status'}
//                         </option>
//                       ))}
//                     </select>
//                   </td>
//                   <td className="px-6 py-4 text-center text-sm">
//                     <textarea
//                       value={row.remarks}
//                       onChange={(e) => handleRemarksChange(row.id, e.target.value)}
//                       className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 w-full resize-y"
//                       rows="2"
//                       placeholder="Enter remarks"
//                       disabled={loading}
//                     />
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
//                   No records found.
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Submit Button - Only shown if user has permission */}
//       <div className="flex justify-end items-center gap-4">
//         {canSubmit && (
//           <button
//             onClick={handleSubmit}
//             className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
//             disabled={loading}
//           >
//             Submit
//           </button>
//         )}
//       </div>
//     </div>
//   );
// };

// export default UncountedModules;